import { useState, useEffect } from 'react';
import { View, StyleSheet, TouchableOpacity, Modal, Text, Dimensions, Image } from 'react-native';
import { useVideoPlayer, VideoView } from 'expo-video';
import { Ionicons } from '@expo/vector-icons';
import { PLACEHOLDER_VIDEO_URL } from '@/lib/constants';
import { trackEvent } from '@/lib/analytics';

interface Props {
  uri?: string | null;
  /**
   * Thumbnail estatica mostrada antes do user clicar pra tocar.
   * Se nao passar, mostra placeholder cinza com icone de play.
   */
  thumbnailUri?: string | null;
  /**
   * Se true, monta o player imediatamente e comeca a tocar (comportamento antigo).
   * Se false (default), mostra thumbnail + botao play; so monta o player apos click.
   *
   * Economiza ~80% de bandwidth quando aluno so quer logar peso sem ver video.
   */
  autoPlay?: boolean;
}

/**
 * Player de video com LAZY LOAD por default.
 *
 * Antes do click no play: renderiza so a thumbnail (zero bytes de video baixados).
 * Apos o click: monta o `useVideoPlayer` real, baixa o video e toca.
 *
 * Usar `autoPlay={true}` apenas em telas onde o video eh o conteudo principal
 * (biblioteca de exercicios). Em modais auxiliares (log de treino), deixar
 * default (lazy).
 */
export function VideoPlayer({ uri, thumbnailUri, autoPlay = false }: Props) {
  const [started, setStarted] = useState(autoPlay);

  if (!started) {
    return (
      <LazyThumbnail
        thumbnailUri={thumbnailUri}
        onPlay={() => {
          trackEvent('student_viewed_video', { source: 'lazy_play' });
          setStarted(true);
        }}
      />
    );
  }

  return <ActiveVideoPlayer uri={uri} autoPlay />;
}

// ─── Sub-componentes ────────────────────────────────────────────────────────

function LazyThumbnail({
  thumbnailUri,
  onPlay,
}: {
  thumbnailUri?: string | null;
  onPlay: () => void;
}) {
  return (
    <TouchableOpacity
      activeOpacity={0.85}
      onPress={onPlay}
      style={styles.container}
    >
      {thumbnailUri ? (
        <Image source={{ uri: thumbnailUri }} style={styles.video} resizeMode="cover" />
      ) : (
        <View style={[styles.video, styles.placeholderBg]} />
      )}
      <View style={styles.playOverlay}>
        <View style={styles.playButton}>
          <Ionicons name="play" size={28} color="#fff" />
        </View>
      </View>
    </TouchableOpacity>
  );
}

function ActiveVideoPlayer({ uri, autoPlay }: { uri?: string | null; autoPlay: boolean }) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const src = uri ?? PLACEHOLDER_VIDEO_URL;

  const player = useVideoPlayer(src, p => {
    p.loop = true;
    p.muted = true; // necessario pra autoplay nao ser bloqueado pelo browser
    if (autoPlay) p.play();
  });
  const fsPlayer = useVideoPlayer(src, p => {
    p.loop = true;
    p.muted = true;
  });

  useEffect(() => {
    if (isFullscreen) fsPlayer.play();
    else fsPlayer.pause();
  }, [isFullscreen]);

  const { width: winW, height: winH } = Dimensions.get('window');

  return (
    <View style={styles.container}>
      <TouchableOpacity
        activeOpacity={0.9}
        onPress={() => {
          trackEvent('student_viewed_video', { source: 'inline_expand' });
          setIsFullscreen(true);
        }}
        style={styles.touchArea}
      >
        <VideoView style={styles.video} player={player} contentFit="contain" />
      </TouchableOpacity>

      <Modal
        visible={isFullscreen}
        animationType="fade"
        transparent={false}
        onRequestClose={() => setIsFullscreen(false)}
      >
        <View style={styles.fsContainer}>
          <VideoView
            style={{ width: winW, height: winH }}
            player={fsPlayer}
            contentFit="contain"
          />
          <TouchableOpacity
            style={styles.fsCloseButton}
            onPress={() => setIsFullscreen(false)}
            hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
          >
            <Text style={styles.fsCloseText}>✕</Text>
          </TouchableOpacity>
        </View>
      </Modal>
    </View>
  );
}

/**
 * Modal de video em tela cheia, controlado externamente.
 * Uso: <FullscreenVideoModal uri={...} visible={...} onClose={...} />
 *
 * Este modal NAO eh lazy — quando visible=true, baixa e toca imediatamente.
 * Faz sentido porque o user EXPLICITAMENTE abriu (clicou em "ver tutorial").
 */
export function FullscreenVideoModal({
  uri,
  visible,
  onClose,
}: {
  uri?: string | null;
  visible: boolean;
  onClose: () => void;
}) {
  const src = uri ?? PLACEHOLDER_VIDEO_URL;
  const player = useVideoPlayer(src, p => {
    p.loop = true;
    p.muted = true;
  });

  useEffect(() => {
    if (visible) player.play();
    else player.pause();
  }, [visible]);

  const { width: winW, height: winH } = Dimensions.get('window');

  return (
    <Modal
      visible={visible}
      animationType="fade"
      transparent={false}
      onRequestClose={onClose}
    >
      <View style={styles.fsContainer}>
        <VideoView
          style={{ width: winW, height: winH }}
          player={player}
          contentFit="contain"
        />
        <TouchableOpacity
          style={styles.fsCloseButton}
          onPress={onClose}
          hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
        >
          <Text style={styles.fsCloseText}>✕</Text>
        </TouchableOpacity>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  container: { alignSelf: 'center', width: 180, height: 320, backgroundColor: '#000', borderRadius: 10, overflow: 'hidden' },
  touchArea: { width: 180, height: 320 },
  video: { width: 180, height: 320 },
  placeholderBg: { backgroundColor: '#1a1a1a' },

  playOverlay: {
    position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
    alignItems: 'center', justifyContent: 'center',
    backgroundColor: 'rgba(0,0,0,0.25)',
  },
  playButton: {
    width: 60, height: 60, borderRadius: 30,
    backgroundColor: 'rgba(200, 16, 46, 0.9)',
    alignItems: 'center', justifyContent: 'center',
    paddingLeft: 4, // compensa offset visual do icone play
  },

  fsContainer: {
    flex: 1, backgroundColor: '#000', justifyContent: 'center', alignItems: 'center',
  },
  fsCloseButton: {
    position: 'absolute', top: 40, right: 20,
    width: 40, height: 40, borderRadius: 20,
    backgroundColor: 'rgba(0,0,0,0.65)',
    justifyContent: 'center', alignItems: 'center',
    zIndex: 10,
  },
  fsCloseText: { color: '#fff', fontSize: 20, fontWeight: '700' },
});
